import { useCallback, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BlockBlobClient } from '@azure/storage-blob';
import {
  deleteApiV1Delft3dOrchestrationsByOrchestrationId,
  postApiV1AnalogueModelsByAnalogueModelIdExternalSimulationByOrchestrationIdComplete,
  postApiV1AnalogueModelsByAnalogueModelIdExternalSimulationInitiate,
} from '../../../../api/generated';
import { queryKeys } from '../../../../hooks/queryKeys';

export type UploadStep =
  | 'idle'
  | 'initiating'
  | 'uploading'
  | 'completing'
  | 'done'
  | 'error';

interface UploadState {
  step: UploadStep;
  progress: number;
  error: string | null;
}

export const useExternalSimulationUpload = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const queryClient = useQueryClient();
  const [state, setState] = useState<UploadState>({
    step: 'idle',
    progress: 0,
    error: null,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const reset = useCallback(() => {
    setState({ step: 'idle', progress: 0, error: null });
  }, []);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    reset();
  }, [reset]);

  const upload = useCallback(
    async (file: File) => {
      if (!modelId) {
        setState({ step: 'error', progress: 0, error: 'Model ID not found' });
        return;
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      let orchestrationId: string | undefined;

      try {
        // Step 1: Initiate
        setState({ step: 'initiating', progress: 0, error: null });

        const initiateResponse =
          await postApiV1AnalogueModelsByAnalogueModelIdExternalSimulationInitiate(
            { path: { analogueModelId: modelId } },
          );

        const responseData =
          'data' in initiateResponse ? initiateResponse.data : undefined;
        const uploadUrl = responseData?.data?.uploadUrl;
        orchestrationId = responseData?.data?.orchestrationId;

        if (!uploadUrl || !orchestrationId) {
          throw new Error(
            'Invalid initiate response: missing uploadUrl or orchestrationId',
          );
        }

        if (abortController.signal.aborted) {
          await deleteApiV1Delft3dOrchestrationsByOrchestrationId({
            path: { orchestrationId },
          }).catch((_err) => {
            // Best-effort cleanup — ignore secondary failures
          });
          return;
        }

        // Step 2: Upload to Azure via BlockBlobClient
        setState({ step: 'uploading', progress: 0, error: null });

        const blobClient = new BlockBlobClient(uploadUrl);

        await blobClient.uploadData(file, {
          abortSignal: abortController.signal,
          blockSize: 4 * 1024 * 1024, // 4MB blocks
          concurrency: 4,
          onProgress: (ev) => {
            const pct = Math.round((ev.loadedBytes / file.size) * 100);
            setState((prev) => ({ ...prev, progress: pct }));
          },
          blobHTTPHeaders: {
            blobContentType: 'application/zip',
          },
        });

        if (abortController.signal.aborted) {
          await deleteApiV1Delft3dOrchestrationsByOrchestrationId({
            path: { orchestrationId },
          }).catch((_err) => {
            // Best-effort cleanup — ignore secondary failures
          });
          return;
        }

        // Step 3: Complete
        setState({ step: 'completing', progress: 100, error: null });

        await postApiV1AnalogueModelsByAnalogueModelIdExternalSimulationByOrchestrationIdComplete(
          { path: { analogueModelId: modelId, orchestrationId } },
        );

        setState({ step: 'done', progress: 100, error: null });
        queryClient.invalidateQueries({ queryKey: queryKeys.scenarios() });
      } catch (err: unknown) {
        // Rollback: delete the orchestration (and its scenario) if it was created,
        // regardless of whether the failure was an abort or an error.
        if (orchestrationId) {
          await deleteApiV1Delft3dOrchestrationsByOrchestrationId({
            path: { orchestrationId },
          }).catch((_err) => {
            // Best-effort cleanup — ignore secondary failures
          });
        }

        if (abortController.signal.aborted) return;

        const message =
          err instanceof Error ? err.message : 'Upload failed unexpectedly';
        setState({ step: 'error', progress: 0, error: message });
      }
    },
    [modelId, queryClient],
  );

  return { ...state, upload, cancel, reset };
};
